import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/userDropdown.css';

const UserDropdown: React.FC = () => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Debug log to verify component is loaded (commented out for production)
  // console.log('UserDropdown component loaded, user:', user);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const navigateToPage = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if ((user as any)?.first_name && (user as any)?.last_name) {
      return `${(user as any).first_name[0]}${(user as any).last_name[0]}`.toUpperCase();
    }
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Get display name (full name for dropdown)
  const getDisplayName = () => {
    if ((user as any)?.first_name) {
      return `${(user as any).first_name} ${(user as any)?.last_name || ''}`.trim();
    }
    return user?.username || 'User';
  };

  // Get greeting name (first name only for friendlier greeting)
  const getGreetingName = () => {
    if ((user as any)?.first_name) {
      return (user as any).first_name;
    }
    return user?.username || 'User';
  };

  // Don't render if user is not authenticated
  if (!user) {
    return null;
  }

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <button 
        className="user-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">
          {getUserInitials()}
        </div>
        <div className="user-info" style={{ minWidth: 'fit-content' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span className="user-greeting" style={{ textAlign: 'left' }}>Hello, {getGreetingName()}</span>
            {(user as any)?.sites?.[0]?.sitename && (
              <span style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.8)',
                textAlign: 'left',
                whiteSpace: 'nowrap'
              }}>
                {(user as any).sites[0].sitename}
              </span>
            )}
          </div>
        </div>
        <i className={`fas fa-caret-${isOpen ? 'up' : 'down'}`} style={{
          fontSize: '12px',
          marginLeft: 'auto',
          color: 'rgba(255,255,255,0.9)'
        }}></i>
      </button>

      {isOpen && (
        <div className="user-dropdown-menu">
          {/* User Info Header */}
          <div className="dropdown-header">
            <div className="user-avatar-large">
              {getUserInitials()}
            </div>
            <div className="user-details">
              <div className="user-name">{getDisplayName()}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="user-email">{user?.email}</div>
                {(user as any)?.email_verified ? (
                  <span style={{
                    fontSize: '11px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: '500',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <i className="fas fa-check-circle" style={{ fontSize: '10px' }}></i>
                    Verified
                  </span>
                ) : (
                  <span style={{
                    fontSize: '11px',
                    backgroundColor: '#ff9f5a',
                    color: '#ffffff',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: '500',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <i className="fas fa-exclamation-circle" style={{ fontSize: '10px' }}></i>
                    Unverified
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="dropdown-divider"></div>

          {/* Account Settings -- COMMENT THIS LINE LATER
          <button 
            className="dropdown-item"
            onClick={() => navigateToPage('/profile')}
          >
            <i className="fas fa-user-cog"></i>
            <span>Account Settings</span>
          </button>

          <div className="dropdown-divider"></div> */}

          {/* Sign Out */}
          <button 
            className="dropdown-item sign-out"
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;